/**
 * COMP-36: Interactive Tournament Bracket Visualization
 * SVG-based interactive bracket with drag-and-drop functionality
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Tournament, Match, Participant } from '../../types/tournament';
import { useBracketInteraction } from '../../hooks/useBracketInteraction';
import { calculateBracketLayout } from '../../utils/bracketLayout';
import {
  MagnifyingGlassPlusIcon as ZoomInIcon,
  MagnifyingGlassMinusIcon as ZoomOutIcon,
  ArrowsPointingOutIcon as ArrowsExpandIcon,
  ArrowPathIcon as _RefreshIcon
} from '@heroicons/react/24/outline';

interface BracketVisualizationProps {
  tournament: Tournament;
  matches: any[];
  participants: any[];
  onMatchUpdate?: (matchId: string, updates: Partial<Match>) => void;
  onParticipantMove?: (participantId: number, newPosition: number) => void;
  isInteractive?: boolean;
  showLiveScores?: boolean;
}

export const BracketVisualization: React.FC<BracketVisualizationProps> = ({
  tournament,
  matches,
  participants,
  onMatchUpdate,
  onParticipantMove,
  isInteractive = true,
  showLiveScores = true
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [bracketLayout, setBracketLayout] = useState<any>(null);
  const [_selectedMatch, _setSelectedMatch] = useState<string | null>(null);
  const [_draggedParticipant, _setDraggedParticipant] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  const {
    handleMatchClick,
    handleParticipantDrag: _handleParticipantDrag,
    handleParticipantDrop: _handleParticipantDrop,
    isValidDrop: _isValidDrop
  } = useBracketInteraction({
    tournament,
    matches,
    participants,
    onMatchUpdate,
    onParticipantMove
  });

  useEffect(() => {
    if (tournament && matches.length > 0) {
      const layout = calculateBracketLayout(tournament, matches, participants);
      setBracketLayout(layout);
    }
  }, [tournament, matches, participants]);

  // Zoom and pan handlers
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.3));
  }, []);

  const handleResetView = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Left mouse button
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  }, [isPanning, lastPanPoint]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoomLevel(prev => Math.max(0.3, Math.min(3, prev * delta)));
  }, []);

  const getParticipantName = (participant: any) => {
    if (!participant) return 'TBD';
    if (participant.name) return participant.name;
    if (participant.first_name && participant.last_name) {
      return `${participant.first_name} ${participant.last_name}`;
    }
    return 'TBD';
  };

  const renderMatch = (match: Match, position: { x: number; y: number }) => {
    const isSelected = _selectedMatch === match.id;
    const isLive = match.status === 'in_progress';

    return (
      <g key={match.id} transform={`translate(${position.x}, ${position.y})`}>
        {/* Match Container */}
        <rect
          width="200"
          height="80"
          rx="8"
          fill={isSelected ? '#3B82F6' : isLive ? '#10B981' : '#F3F4F6'}
          stroke={isSelected ? '#1D4ED8' : '#D1D5DB'}
          strokeWidth="2"
          className="cursor-pointer transition-colors duration-200"
          onClick={() => isInteractive && handleMatchClick(String(match.id))}
        />
        
        {/* Participant 1 */}
        <g transform="translate(10, 15)">
          <rect
            width="180"
            height="25"
            rx="4"
            fill="white"
            stroke="#E5E7EB"
            className={_draggedParticipant === match.participant1Id ? 'opacity-50' : ''}
          />
          <text
            x="10"
            y="17"
            fontSize="12"
            fill="#374151"
            className="font-medium"
          >
            {getParticipantName(participants.find(p => p.id === match.participant1Id))}
          </text>
          {showLiveScores && match.score1 !== undefined && (
            <text
              x="160"
              y="17"
              fontSize="14"
              fill="#1F2937"
              className="font-bold"
            >
              {match.score1}
            </text>
          )}
        </g>
        
        {/* VS Divider */}
        <line
          x1="10"
          y1="40"
          x2="190"
          y2="40"
          stroke="#D1D5DB"
          strokeWidth="1"
        />
        
        {/* Participant 2 */}
        <g transform="translate(10, 45)">
          <rect
            width="180"
            height="25"
            rx="4"
            fill="white"
            stroke="#E5E7EB"
            className={_draggedParticipant === match.participant2Id ? 'opacity-50' : ''}
          />
          <text
            x="10"
            y="17"
            fontSize="12"
            fill="#374151"
            className="font-medium"
          >
            {getParticipantName(participants.find(p => p.id === match.participant2Id))}
          </text>
          {showLiveScores && match.score2 !== undefined && (
            <text
              x="160"
              y="17"
              fontSize="14"
              fill="#1F2937"
              className="font-bold"
            >
              {match.score2}
            </text>
          )}
        </g>
        
        {/* Match Status Indicator */}
        <circle
          cx="185"
          cy="10"
          r="4"
          fill={
            match.status === 'completed' ? '#10B981' :
            match.status === 'in_progress' ? '#F59E0B' :
            '#6B7280'
          }
        />
      </g>
    );
  };

  const renderConnector = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const midX = (from.x + to.x) / 2;
    
    return (
      <path
        d={`M ${from.x} ${from.y} L ${midX} ${from.y} L ${midX} ${to.y} L ${to.x} ${to.y}`}
        stroke="#9CA3AF"
        strokeWidth="2"
        fill="none"
        className="transition-colors duration-200"
      />
    );
  };

  if (!bracketLayout) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading bracket...</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden bg-gray-50 rounded-lg relative cursor-grab"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
    >
      <svg
        ref={svgRef}
        width={bracketLayout.width * zoomLevel}
        height={bracketLayout.height * zoomLevel}
        className="min-w-full min-h-full"
        viewBox={`0 0 ${bracketLayout.width} ${bracketLayout.height}`}
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
          transformOrigin: '0 0'
        }}
      >
        {/* Background Grid */}
        <defs>
          <pattern
            id="grid"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="#F3F4F6"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="url(#grid)"
        />
        
        {/* Render Connectors */}
        {bracketLayout.connectors.map((connector: any, index: number) => (
          <g key={`connector-${index}`}>
            {renderConnector(connector.from, connector.to)}
          </g>
        ))}
        
        {/* Render Matches */}
        {bracketLayout.matches.map((matchLayout: any) => (
          renderMatch(matchLayout.match, matchLayout.position)
        ))}
        
        {/* Round Labels */}
        {bracketLayout.rounds.map((round: any, index: number) => (
          <text
            key={`round-${index}`}
            x={round.x}
            y={round.y}
            fontSize="16"
            fill="#374151"
            className="font-bold"
            textAnchor="middle"
          >
            {round.name}
          </text>
        ))}
      </svg>
      
      {/* Interactive Controls */}
      {isInteractive && (
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-3">
          <div className="flex flex-col space-y-2">
            <button
              className="flex items-center justify-center px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <ZoomInIcon className="h-4 w-4 mr-1" />
              Zoom In
            </button>
            <button
              className="flex items-center justify-center px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <ZoomOutIcon className="h-4 w-4 mr-1" />
              Zoom Out
            </button>
            <button
              className="flex items-center justify-center px-3 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              onClick={handleResetView}
              title="Reset View"
            >
              <ArrowsExpandIcon className="h-4 w-4 mr-1" />
              Reset
            </button>
          </div>

          {/* Zoom Level Indicator */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              Zoom: {Math.round(zoomLevel * 100)}%
            </div>
          </div>
        </div>
      )}

      {/* Live Updates Indicator */}
      {showLiveScores && (
        <div className="absolute top-4 left-4 bg-green-100 text-green-800 px-3 py-2 rounded-lg shadow-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Live Updates</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketVisualization;